import { useTranslation } from 'react-i18next';
import { Card, Chip, Stack, Avatar, Typography, CardHeader, CardContent } from '@mui/material';
import { OrderTypes, ChoicesOrderStatus } from '@/types';
import Iconify from '../../media/iconify';
import { ContactChip } from '../contact-chip';
import Label from '../../core/label';
import { CopyText } from '../../utils/copy-text';
import { OrderStatusLabel } from '@/sections/administrative/accounts/orders/components/order-status-label';

interface OrganizationInfoCardProps {
  organizationData: any;
  contacts: any[];
  currentOrder?: any;
  currentExistsOrder?: any;
  orderNumber?: string;
  sx?: any;
}

export function OrganizationInfoCard({
  organizationData,
  contacts,
  currentOrder,
  currentExistsOrder,
  orderNumber,
  sx,
}: OrganizationInfoCardProps) {
  const { t } = useTranslation();

  const isBillingOrder = currentOrder?.orderType === OrderTypes.BILLING_ORDER;

  return (
    <Card sx={{ mb: 2, ...sx }}>
      <CardHeader
        title={
          <>
            <Iconify icon="solar:buildings-2-linear" width={24} /> {organizationData?.name}{' '}
            <Iconify icon={`circle-flags:${organizationData?.country?.flag_image}`} width={24} />
          </>
        }
        titleTypographyProps={{ variant: 'h3' }}
      />
      <CardContent sx={{ pt: 0.5 }}>
        <Stack spacing={3} direction="row" sx={{ width: '100%' }}>
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Stack spacing={2} direction="row" alignItems="center">
              <Typography
                fontWeight="bold"
                sx={{ minWidth: 120 }}
              >{`${t('Sales Manager')} :`}</Typography>
              <Chip
                avatar={
                  <Avatar
                    alt={organizationData?.owner?.firstName}
                    src={decodeURI(organizationData?.owner?.avatar?.url)}
                  />
                }
                variant="soft"
                size="small"
                label={`${organizationData?.owner?.firstName} ${organizationData?.owner?.lastName}`}
              />
            </Stack>

            <Stack spacing={2} direction="row" alignItems="center">
              <Typography
                fontWeight="bold"
                sx={{ minWidth: 120 }}
              >{`${t('Segment')} :`}</Typography>
              <Typography>{organizationData?.segment?.name}</Typography>
            </Stack>

            <Stack spacing={2} direction="row" alignItems="center">
              <Typography fontWeight="bold" sx={{ minWidth: 120 }}>{`${t('Status')} :`}</Typography>
              <OrderStatusLabel status={currentOrder?.status as ChoicesOrderStatus} />
            </Stack>
          </Stack>

          <Stack spacing={2} sx={{ flex: 1 }}>
            {orderNumber && (
              <Stack spacing={2} direction="row" alignItems="center">
                <Typography fontWeight="bold" sx={{ minWidth: 80 }}>{`${t('Order')} :`}</Typography>
                <CopyText text={orderNumber} />
              </Stack>
            )}

            {isBillingOrder ? (
              <Stack spacing={2} direction="row" alignItems="center">
                <Typography
                  fontWeight="bold"
                  sx={{ minWidth: 80 }}
                >{`${t('Billing Order')} :`}</Typography>
                <CopyText text={currentOrder?.number} />
              </Stack>
            ) : (
              <Stack spacing={2} direction="row" alignItems="center">
                <Typography
                  fontWeight="bold"
                  sx={{ minWidth: 80 }}
                >{`${t('Contacts')} :`}</Typography>
                <Stack spacing={1} direction="row">
                  {contacts?.map((contact: any) => (
                    <ContactChip key={contact._id} contact={contact} />
                  ))}
                </Stack>
              </Stack>
            )}

            {currentOrder?.total && (
              <Stack spacing={2} direction="row" alignItems="center">
                <Typography fontWeight="bold" sx={{ minWidth: 80 }}>{`${t('Total')} :`}</Typography>
                <Typography color="error" fontWeight="bold">
                  ${currentOrder?.total}
                </Typography>
              </Stack>
            )}

            {currentOrder?.order?.status === ChoicesOrderStatus.completed &&
              currentExistsOrder?.OVNumber &&
              currentExistsOrder?.salesOrders?.length === 0 && (
                <Stack spacing={2} direction="row" alignItems="center">
                  <Typography
                    fontWeight="bold"
                    sx={{ minWidth: 80 }}
                  >{`${t('Billing Status')} :`}</Typography>
                  <Label color="warning">{t('Pending')}</Label>
                </Stack>
              )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
